import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  maxHeight?: string;
}

export function Modal({
  isOpen,
  onClose,
  title,
  children,
  maxHeight = '90vh',
}: ModalProps) {
  // Блокируем скролл body при открытой модалке + закрытие по Escape
  useEffect(() => {
    if (!isOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center animate-fade-in"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <button
        type="button"
        aria-label="Закрыть"
        className="absolute inset-0 bg-[var(--backdrop)] no-tap-highlight"
        onClick={onClose}
      />
      <div
        className={cn(
          'relative z-10 w-full max-w-app rounded-t-2xl bg-background',
          'animate-slide-up shadow-md',
        )}
        style={{ maxHeight }}
      >
        <div className="flex items-center justify-between gap-3 px-5 pt-5 pb-3">
          <h2 className="text-lg font-semibold text-text-primary">{title}</h2>
          <button
            type="button"
            aria-label="Закрыть модалку"
            onClick={onClose}
            className={cn(
              'flex h-9 w-9 items-center justify-center rounded-full',
              'text-text-secondary active:bg-surface no-tap-highlight',
            )}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M5 5l10 10M15 5L5 15"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
        <div
          className="overflow-y-auto px-5 pb-6 safe-bottom"
          style={{ maxHeight: `calc(${maxHeight} - 64px)` }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
